import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Calendar, Trash2, ChevronRight, X, Activity, Timer, MapPin, Repeat, BookOpen, Dumbbell, Sparkles, Loader2, PlayCircle } from 'lucide-react';
import { workoutsApi, programsApi } from '@/lib/api';
import { loadDraft, clearDraft } from '@/lib/liveDraft';
import { WorkoutListSkeleton, Skeleton } from '@/components/LoadingSpinner';
import { formatRelativeDate, cn } from '@/lib/utils';
import type { Workout, WorkoutsListResponse, TrainingProgram } from '@/types';

export function HomePage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  // Hae viimeisimmät treenit (max 3)
  const { data: workoutsData, isLoading: workoutsLoading } = useQuery({
    queryKey: ['workouts', 1, 'all'],
    queryFn: () => workoutsApi.list({ page: 1, limit: 3 }),
  });

  // Hae ohjelmat pikatoimintoja varten
  const { data: programsData } = useQuery({
    queryKey: ['programs'],
    queryFn: () => programsApi.list(),
  });

  // Aloita uusi treeni (live-tila)
  const startWorkoutMutation = useMutation({
    mutationFn: () => workoutsApi.create({ startedAt: new Date().toISOString() }),
    onSuccess: (workout) => {
      queryClient.invalidateQueries({ queryKey: ['workouts'] });
      navigate(`/workouts/live/${workout.id}`);
    },
  });

  // Poista treeni
  const deleteMutation = useMutation({
    mutationFn: workoutsApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workouts'] });
      setDeleteConfirm(null);
    },
  });

  const handleDeleteClick = (id: string, e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDeleteConfirm(id);
  };

  const handleConfirmDelete = async () => {
    if (deleteConfirm) {
      await deleteMutation.mutateAsync(deleteConfirm);
    }
  };

  const response = workoutsData as WorkoutsListResponse | undefined;
  const workouts = response?.workouts || [];
  const total = response?.total || 0;
  const lastWorkout = workouts[0];
  const programs = (programsData as { programs: TrainingProgram[] } | undefined)?.programs || [];
  const firstProgram = programs[0];

  if (workoutsLoading) {
    return (
      <div className="max-w-4xl mx-auto space-y-6 animate-fade-in">
        {/* Hero skeleton */}
        <div className="card overflow-hidden">
          <div className="bg-linear-to-br from-primary-500 to-primary-700 dark:from-primary-600 dark:to-primary-800 p-6 sm:p-8">
            <Skeleton className="h-8 w-3/4 mb-2 bg-white/20" />
            <Skeleton className="h-5 w-1/2 mb-6 bg-white/20" />
            <Skeleton className="h-14 w-full rounded-xl bg-white/30" />
          </div>
        </div>
        {/* Quick actions skeleton */}
        <section>
          <Skeleton className="h-6 w-32 mb-3 mx-1" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Skeleton className="h-20 rounded-xl" />
            <Skeleton className="h-20 rounded-xl" />
          </div>
        </section>
        {/* Workouts skeleton */}
        <section>
          <Skeleton className="h-6 w-40 mb-3 mx-1" />
          <WorkoutListSkeleton count={3} />
        </section>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6 animate-fade-in">
      {/* Keskeneräisen treenin paluu-banneri (osio 6.5) */}
      <ResumeWorkoutBanner />

      {/* Hero Section - Aloita treeni */}
      <section className="card overflow-hidden">
        <div className="bg-linear-to-br from-primary-500 to-primary-700 dark:from-primary-600 dark:to-primary-800 p-6 sm:p-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-white mb-2">
            Tervetuloa treenaamaan!
          </h1>
          <p className="text-white/90 mb-6">
            {total > 0
              ? `Olet tehnyt ${total} treeniä. Jatka samaan malliin!`
              : 'Aloita kirjaamalla ensimmäinen treenisi.'
            }
          </p>

          {/* Pääpainike - Aloita treeni */}
          <button
            type="button"
            onClick={() => startWorkoutMutation.mutate()}
            disabled={startWorkoutMutation.isPending}
            className={cn(
              'w-full flex items-center justify-center gap-3',
              'min-h-[56px] px-6 text-lg font-semibold',
              'bg-white text-primary-700 rounded-xl',
              'shadow-lg shadow-primary-900/20',
              'transition-all duration-200 touch-manipulation',
              'hover:bg-primary-50 active:scale-[0.98] active:bg-primary-100',
              'disabled:opacity-70 disabled:active:scale-100',
              'focus:outline-hidden focus-visible:ring-4 focus-visible:ring-white/50'
            )}
            aria-label="Aloita uusi treeni"
          >
            {startWorkoutMutation.isPending ? (
              <Loader2 className="h-6 w-6 animate-spin" aria-hidden="true" />
            ) : (
              <Plus className="h-6 w-6" aria-hidden="true" />
            )}
            {startWorkoutMutation.isPending ? 'Luodaan treeniä...' : 'Aloita uusi treeni'}
          </button>
        </div>
      </section>

      {/* Pikatoiminnot */}
      <section>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-3 px-1">
          Pikatoiminnot
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {/* Toista viimeisin treeni */}
          {lastWorkout && (
            <button
              onClick={() => navigate(`/workouts/${lastWorkout.id}`)}
              className={cn(
                'card flex items-center gap-4 p-4',
                'min-h-[48px] text-left',
                'transition-all duration-200 touch-manipulation',
                'hover:shadow-md active:scale-[0.98] active:bg-gray-50 dark:active:bg-gray-750',
                'focus:outline-hidden focus-visible:ring-2 focus-visible:ring-primary-500'
              )}
              aria-label={`Avaa viimeisin treeni: ${lastWorkout.name || 'Treeni'}`}
            >
              <div className="shrink-0 w-12 h-12 bg-primary-100 dark:bg-primary-900/30 rounded-xl flex items-center justify-center">
                <Repeat className="h-6 w-6 text-primary-600 dark:text-primary-400" aria-hidden="true" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-gray-900 dark:text-white truncate">
                  {lastWorkout.name || 'Viimeisin treeni'}
                </p>
                <p className="text-sm text-gray-500 truncate">
                  {formatRelativeDate(lastWorkout.startedAt || lastWorkout.createdAt)}
                </p>
              </div>
              <ChevronRight className="h-5 w-5 text-gray-400 shrink-0" aria-hidden="true" />
            </button>
          )}

          {/* Treeniohjelmat */}
          {firstProgram ? (
            <button
              onClick={() => navigate(`/programs/${firstProgram.id}`)}
              className={cn(
                'card flex items-center gap-4 p-4',
                'min-h-[48px] text-left',
                'transition-all duration-200 touch-manipulation',
                'hover:shadow-md active:scale-[0.98] active:bg-gray-50 dark:active:bg-gray-750',
                'focus:outline-hidden focus-visible:ring-2 focus-visible:ring-primary-500'
              )}
              aria-label={`Avaa treeniohjelma: ${firstProgram.name}`}
            >
              <div className="shrink-0 w-12 h-12 bg-orange-100 dark:bg-orange-900/30 rounded-xl flex items-center justify-center">
                <BookOpen className="h-6 w-6 text-orange-600 dark:text-orange-400" aria-hidden="true" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-gray-900 dark:text-white truncate">
                  {firstProgram.name}
                </p>
                <p className="text-sm text-gray-500 truncate">
                  {firstProgram.templates?.length || 0} treeniohjetta
                </p>
              </div>
              <ChevronRight className="h-5 w-5 text-gray-400 shrink-0" aria-hidden="true" />
            </button>
          ) : (
            <Link
              to="/programs"
              className={cn(
                'card flex items-center gap-4 p-4',
                'min-h-[48px] text-left',
                'transition-all duration-200 touch-manipulation',
                'hover:shadow-md active:scale-[0.98] active:bg-gray-50 dark:active:bg-gray-750',
                'focus:outline-hidden focus-visible:ring-2 focus-visible:ring-primary-500'
              )}
              aria-label="Luo treeniohjelma"
            >
              <div className="shrink-0 w-12 h-12 bg-orange-100 dark:bg-orange-900/30 rounded-xl flex items-center justify-center">
                <BookOpen className="h-6 w-6 text-orange-600 dark:text-orange-400" aria-hidden="true" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-gray-900 dark:text-white">
                  Luo treeniohjelma
                </p>
                <p className="text-sm text-gray-500">
                  Suunnittele omat treenisi
                </p>
              </div>
              <ChevronRight className="h-5 w-5 text-gray-400 shrink-0" aria-hidden="true" />
            </Link>
          )}
        </div>
      </section>

      {/* Viimeisimmät treenit */}
      <section>
        <div className="flex items-center justify-between mb-3 px-1">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            Viimeisimmät treenit
          </h2>
          {total > 3 && (
            <Link
              to="/workouts"
              className="text-sm text-primary-600 dark:text-primary-400 font-medium hover:underline"
            >
              Näytä kaikki ({total})
            </Link>
          )}
        </div>

        {workouts.length === 0 ? (
          <div className="card p-8 text-center animate-fade-in">
            <div className="relative w-20 h-20 mx-auto mb-4">
              <div className="w-20 h-20 bg-linear-to-br from-primary-100 to-primary-200 dark:from-primary-900/30 dark:to-primary-800/30 rounded-full flex items-center justify-center">
                <Dumbbell className="h-10 w-10 text-primary-600 dark:text-primary-400" aria-hidden="true" />
              </div>
              <div className="absolute -top-1 -right-1 w-8 h-8 bg-yellow-100 dark:bg-yellow-900/30 rounded-full flex items-center justify-center">
                <Sparkles className="h-4 w-4 text-yellow-500" aria-hidden="true" />
              </div>
            </div>
            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
              Aloita treenaaminen!
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6 max-w-sm mx-auto">
              Kirjaa ensimmäinen treenisi ja aloita edistymisen seuraaminen.
            </p>
            <button
              type="button"
              onClick={() => startWorkoutMutation.mutate()}
              disabled={startWorkoutMutation.isPending}
              className={cn(
                'inline-flex items-center justify-center gap-2',
                'min-h-[52px] px-8 font-semibold text-lg',
                'bg-primary-600 text-white rounded-xl',
                'shadow-lg shadow-primary-500/25',
                'transition-all duration-200 touch-manipulation',
                'hover:bg-primary-700 hover:shadow-xl hover:shadow-primary-500/30',
                'active:scale-[0.98] active:shadow-md',
                'disabled:opacity-70 disabled:active:scale-100',
                'focus:outline-hidden focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2'
              )}
            >
              {startWorkoutMutation.isPending ? (
                <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />
              ) : (
                <Plus className="h-5 w-5" aria-hidden="true" />
              )}
              {startWorkoutMutation.isPending ? 'Luodaan treeniä...' : 'Lis��ä ensimmäinen treeni'}
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {workouts.map((workout: Workout, index: number) => (
              <div
                key={workout.id}
                className="animate-fade-in"
                style={{ animationDelay: `${index * 50}ms`, animationFillMode: 'both' }}
              >
                <WorkoutCard
                  workout={workout}
                  onDelete={handleDeleteClick}
                />
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Poiston vahvistus -modaali */}
      {deleteConfirm && (
        <div
          className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-4 animate-in fade-in duration-200"
          onClick={() => setDeleteConfirm(null)}
          role="dialog"
          aria-modal="true"
          aria-labelledby="delete-dialog-title"
        >
          <div
            className="bg-white dark:bg-gray-800 rounded-t-2xl sm:rounded-2xl shadow-xl max-w-sm w-full p-6 animate-in slide-in-from-bottom-4 sm:slide-in-from-bottom-0 sm:zoom-in-95 duration-300"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h2 id="delete-dialog-title" className="text-lg font-semibold text-gray-900 dark:text-white">
                Poista treeni?
              </h2>
              <button
                onClick={() => setDeleteConfirm(null)}
                className={cn(
                  'p-2 text-gray-400 rounded-lg',
                  'transition-colors touch-manipulation',
                  'hover:text-gray-600 hover:bg-gray-100',
                  'dark:hover:text-gray-300 dark:hover:bg-gray-700',
                  'active:bg-gray-200 dark:active:bg-gray-600',
                  'focus:outline-hidden focus-visible:ring-2 focus-visible:ring-primary-500'
                )}
                aria-label="Sulje vahvistusikkuna"
              >
                <X className="h-5 w-5" aria-hidden="true" />
              </button>
            </div>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Haluatko varmasti poistaa tämän treenin? Toimintoa ei voi perua.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className={cn(
                  'flex-1 min-h-[48px] px-4 font-medium rounded-xl',
                  'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300',
                  'transition-all duration-200 touch-manipulation',
                  'hover:bg-gray-200 dark:hover:bg-gray-600',
                  'active:scale-[0.98] active:bg-gray-300 dark:active:bg-gray-500',
                  'focus:outline-hidden focus-visible:ring-2 focus-visible:ring-primary-500'
                )}
              >
                Peruuta
              </button>
              <button
                onClick={handleConfirmDelete}
                disabled={deleteMutation.isPending}
                className={cn(
                  'flex-1 min-h-[48px] px-4 font-medium rounded-xl',
                  'bg-red-600 text-white',
                  'transition-all duration-200 touch-manipulation',
                  'hover:bg-red-700',
                  'active:scale-[0.98] active:bg-red-800',
                  'disabled:opacity-50 disabled:cursor-not-allowed disabled:active:scale-100',
                  'focus:outline-hidden focus-visible:ring-2 focus-visible:ring-red-500'
                )}
              >
                {deleteMutation.isPending ? 'Poistetaan...' : 'Poista'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Keskeneräisen live-treenin paluu-banneri (osio 6.5).
// Osoitin luetaan localStoragesta, mutta olemassaolo/valmius varmistetaan aina
// palvelimelta ennen näyttämistä — vanhentunut osoitin siivotaan hiljaisesti.
function ResumeWorkoutBanner() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState(() => loadDraft());
  const [confirmDiscard, setConfirmDiscard] = useState(false);

  const { data: workout, isError, isLoading } = useQuery({
    queryKey: ['workout', draft?.workoutId],
    queryFn: () => workoutsApi.get(draft!.workoutId),
    enabled: !!draft,
    retry: false,
  });

  const discardMutation = useMutation({
    mutationFn: () => workoutsApi.delete(draft!.workoutId),
    onSuccess: () => {
      clearDraft();
      setDraft(null);
      setConfirmDiscard(false);
      queryClient.invalidateQueries({ queryKey: ['workouts'] });
    },
  });

  // Siivoa vanhentunut osoitin (treeni poistettu tai jo valmis).
  useEffect(() => {
    if (!draft) return;
    if (isError || (workout && workout.completedAt)) {
      clearDraft();
      setDraft(null);
    }
  }, [isError, workout, draft]);

  if (!draft || isLoading || isError || workout?.completedAt) return null;

  const started = new Date(draft.startedAt);
  const hh = started.getHours().toString().padStart(2, '0');
  const mm = started.getMinutes().toString().padStart(2, '0');
  const workoutName = workout?.name || draft.name || 'Treeni';

  return (
    <section
      className="card border-primary-200 dark:border-primary-900/40 bg-primary-50/50 dark:bg-primary-900/10 p-4"
      aria-label="Keskeneräinen treeni"
    >
      <div className="flex items-start gap-3">
        <div className="shrink-0 w-10 h-10 rounded-xl bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
          <PlayCircle className="h-5 w-5 text-primary-600 dark:text-primary-400" aria-hidden="true" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-gray-900 dark:text-white truncate">
            Keskeneräinen treeni
          </p>
          <p className="text-sm text-gray-500">
            {workoutName} · aloitettu klo {hh}:{mm}
          </p>
          <div className="flex gap-2 mt-3">
            <button
              type="button"
              onClick={() => navigate(`/workouts/live/${draft.workoutId}`)}
              className="btn btn-primary min-h-[44px]"
            >
              Jatka
            </button>
            <button
              type="button"
              onClick={() => setConfirmDiscard(true)}
              className="btn btn-secondary min-h-[44px]"
            >
              Hylkää
            </button>
          </div>
        </div>
      </div>

      {/* Hylkäyksen vahvistus */}
      {confirmDiscard && (
        <div
          className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-4"
          onClick={() => setConfirmDiscard(false)}
          role="dialog"
          aria-modal="true"
          aria-label="Hylkää keskeneräinen treeni"
        >
          <div
            className="bg-white dark:bg-gray-800 rounded-t-2xl sm:rounded-2xl shadow-xl max-w-sm w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              Hylkää keskeneräinen treeni?
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Keskeneräinen treeni poistetaan pysyvästi. Toimintoa ei voi perua.
            </p>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setConfirmDiscard(false)}
                className="btn btn-secondary flex-1 min-h-[48px]"
              >
                Peruuta
              </button>
              <button
                type="button"
                onClick={() => discardMutation.mutate()}
                disabled={discardMutation.isPending}
                className="btn btn-danger flex-1 min-h-[48px]"
              >
                {discardMutation.isPending ? 'Poistetaan...' : 'Hylkää'}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

// Aktiviteettityypit
const CARDIO_ACTIVITY_TYPES = ['walking', 'running', 'cycling', 'swimming', 'hiking', 'elliptical', 'stair_climbing', 'rowing', 'snowboarding', 'resort_skiing_snowboarding'];
const STRENGTH_ACTIVITY_TYPES = ['strength', 'weight', 'gym', 'crossfit'];

function isCardioActivity(activityType: string): boolean {
  return CARDIO_ACTIVITY_TYPES.some(type => activityType.toLowerCase().includes(type));
}

function isStrengthActivity(activityType: string): boolean {
  return STRENGTH_ACTIVITY_TYPES.some(type => activityType.toLowerCase().includes(type));
}

function getActivityTypeLabel(activityType: string): string {
  const lower = activityType.toLowerCase();
  if (lower.includes('walking')) return 'Kävely';
  if (lower.includes('running')) return 'Juoksu';
  if (lower.includes('cycling')) return 'Pyöräily';
  if (lower.includes('swimming')) return 'Uinti';
  if (lower.includes('hiking')) return 'Vaellus';
  if (lower.includes('strength') || lower.includes('weight')) return 'Voimaharjoittelu';
  if (lower.includes('gym')) return 'Kuntosali';
  return activityType;
}

function formatDistance(meters: number): string {
  if (meters >= 1000) {
    return `${(meters / 1000).toFixed(2)} km`;
  }
  return `${Math.round(meters)} m`;
}

function formatDuration(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const mins = Math.round(minutes % 60);
  if (hours > 0) {
    return `${hours} h ${mins} min`;
  }
  return `${mins} min`;
}

// Kompakti treenikortti
function WorkoutCard({
  workout,
  onDelete,
}: {
  workout: Workout;
  onDelete: (id: string, e: React.MouseEvent | React.TouchEvent) => void;
}) {
  const workoutDate = workout.startedAt || workout.createdAt;
  const cardioActivity = workout.wearableActivities?.find(a => isCardioActivity(a.activityType));
  const strengthActivity = workout.wearableActivities?.find(a => isStrengthActivity(a.activityType));
  const hasManualSets = workout.exercises && workout.exercises.length > 0;
  const isCardioWorkout = !hasManualSets && cardioActivity;
  const isStrengthWorkout = !hasManualSets && strengthActivity;

  // Määritä kortin väritys
  const cardColorClass = isCardioWorkout
    ? 'border-l-4 border-l-primary-500'
    : isStrengthWorkout
      ? 'border-l-4 border-l-orange-500'
      : '';

  return (
    <Link
      to={`/workouts/${workout.id}`}
      className={cn(
        'card block',
        'transition-all duration-200 touch-manipulation',
        'hover:shadow-md active:scale-[0.99] active:bg-gray-50 dark:active:bg-gray-750',
        'focus:outline-hidden focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2',
        cardColorClass
      )}
      aria-label={`Avaa treeni: ${workout.name || 'Treeni'}, ${formatRelativeDate(workoutDate)}`}
    >
      <div className="p-4">
        <div className="flex items-start gap-4">
          {/* Ikoni */}
          <div className={cn(
            'shrink-0 w-10 h-10 rounded-lg flex items-center justify-center',
            isCardioWorkout
              ? 'bg-primary-100 dark:bg-primary-900/30'
              : isStrengthWorkout
                ? 'bg-orange-100 dark:bg-orange-900/30'
                : 'bg-gray-100 dark:bg-gray-800'
          )}>
            {isCardioWorkout ? (
              <Activity className="h-5 w-5 text-primary-600 dark:text-primary-400" aria-hidden="true" />
            ) : (
              <Dumbbell className="h-5 w-5 text-gray-600 dark:text-gray-400" aria-hidden="true" />
            )}
          </div>

          {/* Sisältö */}
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-gray-900 dark:text-white truncate">
              {workout.name || 'Treeni'}
            </h3>

            <div className="flex items-center gap-2 mt-1 text-sm text-gray-500">
              <Calendar className="h-3.5 w-3.5" aria-hidden="true" />
              <span>{formatRelativeDate(workoutDate)}</span>
              {isCardioWorkout && cardioActivity?.durationMinutes && (
                <>
                  <span className="text-gray-300 dark:text-gray-600" aria-hidden="true">•</span>
                  <Timer className="h-3.5 w-3.5" aria-hidden="true" />
                  <span>{formatDuration(cardioActivity.durationMinutes)}</span>
                </>
              )}
              {isCardioWorkout && cardioActivity?.distance && Number(cardioActivity.distance) > 0 && (
                <>
                  <span className="text-gray-300 dark:text-gray-600" aria-hidden="true">•</span>
                  <MapPin className="h-3.5 w-3.5" aria-hidden="true" />
                  <span>{formatDistance(Number(cardioActivity.distance))}</span>
                </>
              )}
            </div>

            {/* Manuaaliset sarjat - kompakti */}
            {hasManualSets && (
              <p className="text-sm text-gray-500 mt-1">
                {workout.exercises!.length} liikettä
              </p>
            )}
          </div>

          {/* Toiminnot */}
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={(e) => onDelete(workout.id, e)}
              onTouchEnd={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onDelete(workout.id, e);
              }}
              className={cn(
                'p-3 rounded-lg text-gray-400',
                'transition-all duration-200 touch-manipulation',
                'hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20',
                'active:scale-95 active:text-red-600 active:bg-red-100 dark:active:bg-red-900/30',
                'focus:outline-hidden focus-visible:ring-2 focus-visible:ring-red-500'
              )}
              aria-label="Poista treeni"
            >
              <Trash2 className="h-5 w-5" aria-hidden="true" />
            </button>
            <ChevronRight className="h-5 w-5 text-gray-300 dark:text-gray-600" aria-hidden="true" />
          </div>
        </div>
      </div>
    </Link>
  );
}
